/**
 * Extensions Fluxmed de documentos de suporte.
 *
 * Decisão FHIR (2026-09):
 * - Validade = extension `supporting-document/valid-until` (valueDate YYYY-MM-DD)
 *   no attester da Organization (não na raiz do DocumentReference).
 *   Assim a aprovação de uma clínica não vale para outra.
 * - Vínculo transacional = extension `supporting-document/transaction-id`
 *   (valueString UUID) na raiz do DocumentReference + MedicationDispense.
 *
 * Documento sem `transaction-id` é tratado como histórico (modelo antigo).
 * Não há pareamento legado: o usuário só vê documentos do modelo atual.
 *
 * @module documentExtensions
 */

import dayjs from "dayjs";

export const DOCUMENT_VALID_UNTIL_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/extension/supporting-document/valid-until";

export const DOCUMENT_TRANSACTION_ID_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/extension/supporting-document/transaction-id";

export interface DocumentExtensionLike {
  url?: string;
  valueString?: string;
  valueDate?: string;
}

export interface DocumentWithExtensionsLike {
  extension?: DocumentExtensionLike[];
}

/** Lê a última extension `valid-until` de um array (attester ou raiz). */
export function extractValidUntilFromExtensions(
  extensions: DocumentExtensionLike[] | undefined,
): string | undefined {
  const matches = extensions?.filter(
    (ext) => ext.url === DOCUMENT_VALID_UNTIL_EXTENSION_URL,
  );
  const last = matches?.[matches.length - 1];
  const value = last?.valueDate?.trim() || last?.valueString?.trim();
  return value || undefined;
}

/** Fallback raiz (aprovações antigas, antes da validade ir para o attester). */
export function extractDocumentValidUntil(
  doc: DocumentWithExtensionsLike | undefined,
): string | undefined {
  return extractValidUntilFromExtensions(doc?.extension);
}

/** Lê a última extension `transaction-id`. */
export function extractDocumentTransactionId(
  doc: DocumentWithExtensionsLike | undefined,
): string | undefined {
  const matches = doc?.extension?.filter(
    (ext) => ext.url === DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
  );
  const last = matches?.[matches.length - 1];
  return last?.valueString?.trim() || undefined;
}

/** Documento do modelo antigo: sem extension `transaction-id`. */
export function isLegacySupportingDocument(
  doc: DocumentWithExtensionsLike | undefined,
): boolean {
  return !extractDocumentTransactionId(doc);
}

/**
 * Vencido somente quando há `valid-until` e ela é anterior a hoje.
 * Sem a extension não vence.
 */
export function isDocumentValidityExpired(
  doc: DocumentWithExtensionsLike | undefined,
  todayYmd = dayjs().format("YYYY-MM-DD"),
): boolean {
  const validUntil = extractDocumentValidUntil(doc);
  if (!validUntil) return false;
  const parsed = dayjs(validUntil);
  if (!parsed.isValid()) return false;
  return parsed.isBefore(dayjs(todayYmd), "day");
}

export function isDocumentValidityExpiredDate(
  validUntil: string | undefined,
  todayYmd = dayjs().format("YYYY-MM-DD"),
): boolean {
  if (!validUntil?.trim()) return false;
  const parsed = dayjs(validUntil);
  if (!parsed.isValid()) return false;
  return parsed.isBefore(dayjs(todayYmd), "day");
}

export function buildValidUntilExtension(validUntil: string): DocumentExtensionLike {
  return {
    url: DOCUMENT_VALID_UNTIL_EXTENSION_URL,
    valueDate: validUntil.trim(),
  };
}

export function buildTransactionIdExtension(
  transactionId: string,
): DocumentExtensionLike {
  return {
    url: DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
    valueString: transactionId.trim(),
  };
}

/** Gera UUID estável (browser) com fallback determinístico. */
export function createDocumentTransactionId(): string {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  return `tx-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Merge na raiz do DocumentReference: só `transaction-id`.
 * `valid-until` na raiz é removido (passa a viver no attester da org).
 */
export function mergeDocumentValidityExtensions(
  current: DocumentExtensionLike[] | undefined,
  params: { validUntil?: string; transactionId?: string },
): DocumentExtensionLike[] {
  const next = (current ?? []).filter(
    (ext) =>
      ext.url !== DOCUMENT_VALID_UNTIL_EXTENSION_URL &&
      ext.url !== DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
  );
  if (params.transactionId?.trim()) {
    next.push(buildTransactionIdExtension(params.transactionId));
  }
  return next;
}
